import { useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { Button, Modal, Radio, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { DocumentLinePicker } from '@/components/client-deliverables/DocumentLinePicker';
import type { ClientDeliverable, LinkCandidate } from '@/lib/types';

/**
 * LINKING A STANDALONE ROW TO A DOCUMENT LINE — owner 2026-10-16.
 *
 * `POST /client-deliverables/:id/link` folds a hand-entered row into the row a
 * deal, quote or invoice line already derives, carrying the typed progress
 * across. Two outcomes, neither of which this dialog words: `merged` (the
 * standalone row is gone and its fields sit on the document row) and
 * `deal-linked` (the row stays, attached to the deal line, until a quote or
 * invoice is raised from it). Either way the page re-reads — the row's `key`
 * changes, so nothing on screen can be patched in place.
 *
 * ⚠️ **409 `LINK_CONFLICT` is not a failure, it is a question.** Both rows
 * carry a value for the same field, and the server will not pick one. Its
 * `details` name each field with both values; the reader chooses PER FIELD and
 * the request is re-sent with `resolutions`. There is deliberately NO default
 * choice — a pre-ticked side would make "Link" a one-click overwrite of
 * somebody's typed progress, which is the loss this whole step exists to stop.
 *
 * ⚠️ Every other refusal (`DEAL_LINE_ALREADY_LINKED`, `LINE_HAS_PIECES`,
 * `LINK_MISMATCH`, 403, 404) is the server's own sentence in a danger notice.
 * Matched on `error.code`, never on the status: a 409 here means three
 * different things.
 *
 * ⚠️ The conflict rows are read from `detailPayload`, not `details`: the
 * latter is typed as the validation shape and does not declare `manual` /
 * `document`, so reading them through it would be a cast that hides a wire
 * change.
 */

export interface LinkDeliverableModalProps {
  open: boolean;
  row: ClientDeliverable | null;
  onClose: () => void;
  /** The page re-reads: a link changes which rows exist. */
  onLinked: () => void;
}

type LinkChoice = { kind: LinkCandidate['kind']; lineId: LinkCandidate['lineId'] };
type Side = 'manual' | 'document';

interface Conflict {
  path: string;
  message: string;
  manual: unknown;
  document: unknown;
}

/** Labels for the typed fields a conflict can name; anything else shows its path. */
const FIELD_LABELS: Record<string, string> = {
  completed: 'Completed',
  promised: 'Promised',
  promisedOverride: 'Promised',
  deliveryLeadId: 'POC',
  deliveryLead: 'POC',
  specialistId: 'Specialist',
  specialist: 'Specialist',
  bookingSpec: 'Booking',
  travelOverride: 'Travel',
  travel: 'Travel',
  startDate: 'Start date',
  endDate: 'End date',
  billingNote: 'Billing note',
  adsPlan: 'Ads plan',
  engagementRole: 'Engagement role',
};

function readConflicts(caught: ApiError): Conflict[] {
  const raw = caught.detailPayload;
  if (!Array.isArray(raw)) return [];
  const conflicts: Conflict[] = [];
  for (const entry of raw as unknown[]) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Record<string, unknown>;
    if (typeof record.path !== 'string' || record.path === '') continue;
    conflicts.push({
      path: record.path,
      message: typeof record.message === 'string' ? record.message : '',
      manual: record.manual,
      document: record.document,
    });
  }
  return conflicts;
}

/** A conflict value as text. `null`, `undefined` and `''` are all "not set". */
function valueText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) {
    const parts = value.map(valueText).filter((part) => part !== '—');
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['name', 'label', 'productName', 'email']) {
      if (typeof record[key] === 'string' && record[key] !== '') return record[key];
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function refusalOf(caught: unknown): string {
  if (!(caught instanceof ApiError)) {
    return caught instanceof Error ? caught.message : 'Could not reach the server. Check your connection.';
  }
  if (caught.status === 403) {
    return 'Your permission set does not include this. An administrator can change that.';
  }
  return caught.formMessage ?? Object.values(caught.fieldErrors)[0] ?? caught.message;
}

export function LinkDeliverableModal(props: LinkDeliverableModalProps) {
  const { open, row } = props;
  if (!open || row === null || row.id === null) return null;
  // Keyed on the row so a second row never inherits the first one's choices.
  return <LinkBody key={row.key} {...props} row={row} rowId={row.id} />;
}

function LinkBody({
  row,
  rowId,
  onClose,
  onLinked,
}: LinkDeliverableModalProps & { row: ClientDeliverable; rowId: number }) {
  const [candidates, setCandidates] = useState<LinkCandidate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState<LinkChoice | null>(null);
  const [conflicts, setConflicts] = useState<Conflict[] | null>(null);
  const [resolutions, setResolutions] = useState<Record<string, Side>>({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listDeliverableLinkCandidates(rowId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCandidates(result.data);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setCandidates(null);
        setLoadError(refusalOf(caught));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [rowId]);

  function pick(next: LinkChoice | null) {
    setChoice(next);
    // A conflict belongs to the line it was raised against.
    setConflicts(null);
    setResolutions({});
    setFormError(null);
  }

  const unresolved = conflicts?.filter((conflict) => resolutions[conflict.path] === undefined) ?? [];
  const blockedReason: string | null =
    choice === null
      ? 'Choose a line to link to.'
      : unresolved.length > 0
        ? 'Choose which value to keep for every field.'
        : null;

  async function submit() {
    if (choice === null || blockedReason !== null || saving) return;
    setSaving(true);
    setFormError(null);
    // Only the fields the server named; `blockedReason` has proved each is chosen.
    let sentResolutions: Record<string, Side> | undefined;
    if (conflicts && conflicts.length > 0) {
      sentResolutions = {};
      for (const conflict of conflicts) {
        const side = resolutions[conflict.path];
        if (side !== undefined) sentResolutions[conflict.path] = side;
      }
    }
    try {
      await api.linkClientDeliverable(rowId, {
        kind: choice.kind,
        lineId: choice.lineId,
        ...(sentResolutions ? { resolutions: sentResolutions } : {}),
      });
      onLinked();
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'LINK_CONFLICT') {
        const found = readConflicts(caught);
        if (found.length > 0) {
          setConflicts(found);
          // Keep a choice already made for a field the server names again.
          setResolutions((prev) => {
            const kept: Record<string, Side> = {};
            for (const conflict of found) {
              const side = prev[conflict.path];
              if (side !== undefined) kept[conflict.path] = side;
            }
            return kept;
          });
          setSaving(false);
          return;
        }
      }
      setFormError(refusalOf(caught));
      setSaving(false);
    }
  }

  const rowName = [row.company?.name, row.product?.productName].filter(Boolean).join(' · ');

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      dismissOnBackdrop={false}
      title={rowName ? `Link ${rowName}` : 'Link this deliverable'}
      footer={
        <>
          {blockedReason && (
            <span className="self-center text-xs text-fg-muted sm:mr-auto">{blockedReason}</span>
          )}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={saving}
            disabled={blockedReason !== null}
          >
            Link
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && (
          <Notice tone="danger" role="alert" className="break-words">
            {formError}
          </Notice>
        )}

        <DocumentLinePicker
          candidates={candidates}
          loading={loading}
          error={loadError}
          value={choice}
          onChange={pick}
        />

        {conflicts && conflicts.length > 0 && (
          <section aria-labelledby="link-conflicts-heading" className="space-y-2">
            <h3 id="link-conflicts-heading" className="m-0 text-sm font-semibold text-fg">
              Both rows have a value for {conflicts.length === 1 ? 'this field' : 'these fields'}
            </h3>

            {/* Below md the table becomes one card per field, same columns. */}
            <ul className="m-0 list-none space-y-2 p-0 md:hidden">
              {conflicts.map((conflict) => (
                <li key={conflict.path} className="rounded-md border border-border p-3">
                  <p className="m-0 mb-2 text-sm font-medium text-fg">
                    {FIELD_LABELS[conflict.path] ?? conflict.path}
                  </p>
                  <div className="grid gap-2">
                    <ConflictRadio conflict={conflict} side="manual" resolutions={resolutions} onPick={setResolutions} disabled={saving} labelled />
                    <ConflictRadio conflict={conflict} side="document" resolutions={resolutions} onPick={setResolutions} disabled={saving} labelled />
                  </div>
                </li>
              ))}
            </ul>

            <div className="hidden md:block">
              <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
                <Table>
                  <THead>
                    <TR>
                      <TH>Field</TH>
                      <TH>This row</TH>
                      <TH>Document row</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {conflicts.map((conflict) => (
                      <TR key={conflict.path}>
                        <TD className="font-medium text-fg">
                          {FIELD_LABELS[conflict.path] ?? conflict.path}
                        </TD>
                        <TD>
                          <ConflictRadio conflict={conflict} side="manual" resolutions={resolutions} onPick={setResolutions} disabled={saving} />
                        </TD>
                        <TD>
                          <ConflictRadio conflict={conflict} side="document" resolutions={resolutions} onPick={setResolutions} disabled={saving} />
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}

/**
 * One side of one conflicted field. The two layouts render the same radios
 * under the same `name`, and only one layout is visible, so the checked state
 * is driven from `resolutions` rather than from the DOM.
 */
function ConflictRadio({
  conflict,
  side,
  resolutions,
  onPick,
  disabled,
  labelled = false,
}: {
  conflict: Conflict;
  side: Side;
  resolutions: Record<string, Side>;
  onPick: (update: (prev: Record<string, Side>) => Record<string, Side>) => void;
  disabled: boolean;
  /** The card layout has no column headers, so each radio says which row it is. */
  labelled?: boolean;
}) {
  const value = valueText(side === 'manual' ? conflict.manual : conflict.document);
  const rowLabel = side === 'manual' ? 'This row' : 'Document row';
  return (
    <Radio
      name={`${labelled ? 'card' : 'table'}-conflict-${conflict.path}`}
      value={side}
      checked={resolutions[conflict.path] === side}
      disabled={disabled}
      onChange={() => onPick((prev) => ({ ...prev, [conflict.path]: side }))}
      aria-label={labelled ? undefined : `${FIELD_LABELS[conflict.path] ?? conflict.path}: ${rowLabel}`}
      label={
        labelled ? (
          <>
            <span className="text-fg-muted">{rowLabel}: </span>
            <span className="[overflow-wrap:anywhere]">{value}</span>
          </>
        ) : (
          <span className="[overflow-wrap:anywhere]">{value}</span>
        )
      }
    />
  );
}
